
import React, { useState, useRef, useEffect } from "react";
import { useEtymology } from "@/hooks/useEtymology";
import EtymologyForm from "@/components/EtymologyForm";
import EtymologyResult from "@/components/EtymologyResult";
import LoadingScriptures from "@/components/LoadingScriptures";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { useScrollToTop } from "@/hooks/useScrollToTop";

const Etymology = () => {
  useScrollToTop();

  const {
    term,
    setTerm,
    isLoading,
    isSaving,
    etymology,
    handleSubmit,
    saveEtymology,
  } = useEtymology();
  
  const resultRef = useRef<HTMLDivElement>(null);
  
  // Scroll to results when they load
  useEffect(() => {
    if (etymology && !isLoading && resultRef.current) {
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [etymology, isLoading]);

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      {/* Hero Section */}
      <div className="pt-24 pb-16 text-center">
        <h1 className="text-5xl md:text-7xl font-playfair mb-6">
          <span className="text-black">Biblical </span>
          <span className="text-heaven-gold">Etymology</span>
        </h1>
        <p className="text-gray-600 max-w-3xl mx-auto px-4 text-lg leading-relaxed">
          Explore the rich, layered meaning behind any word or phrase found in Scripture by 
          tracing it back to its original biblical languages — Hebrew, Greek, Aramaic, and Latin.
        </p>
      </div>

      {/* Search Form Container */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        <div className="bg-white rounded-2xl shadow-md p-8">
          <h2 className="text-3xl text-[#4169E1] text-center mb-3">
            What Word Would You Like to Explore?
          </h2>
          <p className="text-gray-600 text-center mb-6">
            Enter a biblical word or phrase to discover its roots and meaning across ancient languages.
          </p>
          
          <EtymologyForm
            term={term}
            isLoading={isLoading}
            onTermChange={setTerm}
            onSubmit={handleSubmit}
          />
        </div>

        {/* Results Section */}
        {isLoading ? (
          <LoadingScriptures />
        ) : (
          etymology && (
            <div ref={resultRef} className="mt-8">
              <div className="mb-8">
                <h2 className="text-2xl font-playfair font-bold text-gray-800 mb-2">
                  Etymology of "{term}"
                </h2>
                <p className="text-gray-600">
                  Exploring the ancient roots and biblical meaning.
                </p>
              </div>
              
              <EtymologyResult etymology={etymology} />
              <div className="flex justify-end mt-6">
                <Button
                  onClick={saveEtymology}
                  disabled={isSaving}
                  className="bg-heaven-gold hover:bg-heaven-gold/80 text-white"
                >
                  {isSaving ? (
                    <>Saving...</>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save to Dashboard
                    </>
                  )}
                </Button>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Etymology;
